/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { DialogClose } from "@radix-ui/react-dialog";
import {
  type ForwardedRef,
  forwardRef,
  type ReactNode,
  type SyntheticEvent,
  useEffect,
  useState,
} from "react";

import { useDynamicViewport } from "../../../../hooks/useDynamicViewport";
import { VIEWPORT_TABLET } from "../../../../lib/constants/viewports/viewportTablet";
import type { DropdownMenuItemProps } from "../DropdownMenuItem/DropdownMenuItem.emotion";
import { DropdownMenuItem } from "../DropdownMenuItem/DropdownMenuItem.emotion";
import type { ModalItemProps } from "../ModalItem/ModalItem.emotion";
import { ModalItem } from "../ModalItem/ModalItem.emotion";

export type ModalDropdownMenuItemProps = {
  children?: ReactNode;
  dropdownMenuItemCSS?: Interpolation;
  dropdownMenuItemProps?: DropdownMenuItemProps;
  modalItemCSS?: Interpolation;
  modalItemProps?: ModalItemProps;
  selected?: boolean;
  onSelect?: (e: Event | SyntheticEvent<HTMLButtonElement>) => void;
  minDesktopWidth?: number;
  options?: {
    closeModalOnSelect?: boolean;
  };
};

export const ModalDropdownMenuItem = forwardRef<
  HTMLButtonElement | HTMLDivElement,
  ModalDropdownMenuItemProps
>(
  (
    {
      children,
      dropdownMenuItemCSS = {},
      dropdownMenuItemProps = {} as NonNullable<
        ModalDropdownMenuItemProps["dropdownMenuItemProps"]
      >,
      modalItemCSS = {},
      modalItemProps = {} as NonNullable<
        ModalDropdownMenuItemProps["modalItemProps"]
      >,
      selected,
      onSelect,
      minDesktopWidth = VIEWPORT_TABLET,
      options = {} as NonNullable<ModalDropdownMenuItemProps["options"]>,
      ...props
    },
    ref,
  ) => {
    const { closeModalOnSelect = true } = options;

    const [isMounted, setIsMounted] = useState(false);
    useEffect(() => setIsMounted(true), []);

    const isDesktopViewport = useDynamicViewport({
      minimumWidth: minDesktopWidth,
      defaultState: undefined,
    });

    if (!isMounted) return null;

    if (isDesktopViewport)
      return (
        <DropdownMenuItem
          ref={ref as ForwardedRef<HTMLDivElement>}
          selected={selected}
          onSelect={onSelect}
          {...props}
          css={dropdownMenuItemCSS}
          {...dropdownMenuItemProps}
        >
          {dropdownMenuItemProps.children || children}
        </DropdownMenuItem>
      );

    const ModalItemComponent = (
      <ModalItem
        ref={ref as ForwardedRef<HTMLButtonElement>}
        selected={selected}
        onSelect={onSelect}
        {...props}
        css={modalItemCSS}
        {...modalItemProps}
      >
        {modalItemProps.children || children}
      </ModalItem>
    );

    if (closeModalOnSelect) {
      return <DialogClose asChild>{ModalItemComponent}</DialogClose>;
    } else {
      return ModalItemComponent;
    }
  },
);

ModalDropdownMenuItem.displayName = "ModalDropdownMenuItem";
